/**
 * API Client for OpenSearch Agent Backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TOKEN_KEY = 'labv_agent_token';

function parseJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function isTokenValid() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) return false;
  return payload.exp > Math.floor(Date.now() / 1000);
}

export function storeToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

function authHeaders(contentTypeJson = false) {
  const h = {};
  if (contentTypeJson) {
    h['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

export async function loginWithLabV(username, password) {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  const { token } = await response.json();
  storeToken(token);
  return token;
}

/**
 * Query the agent with a natural language question.
 * The data profile is resolved server-side from the JWT `company` claim.
 * @param {string} question - The question to ask
 * @param {string} sessionId - Conversation session identifier for memory scoping
 * @param {Object} options - Agent behavior options
 * @param {boolean} options.evaluate - Enable the evaluator node
 * @param {boolean} options.allowRetry - Allow evaluator-driven retries
 * @param {number} options.maxRetries - Maximum number of retries (honoured only when allowRetry is true)
 * @returns {Promise} Response from the agent
 */
export async function queryAgent(question, sessionId = 'default', options = {}) {
  const { evaluate = false, allowRetry = false, maxRetries = 0 } = options;
  const body = {
    question,
    session_id: sessionId,
    evaluate,
    allow_retry: allowRetry,
    max_retries: maxRetries,
  };
  const response = await fetch(`${API_BASE_URL}/api/query`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Stream a query over a WebSocket. Preferred transport: cancellation rides the same
 * connection, so a stop takes effect immediately without a second HTTP request.
 *
 * A browser cannot set an Authorization header on a WS handshake, so the JWT travels in the
 * opening frame — which also keeps it out of URLs and access logs.
 *
 * @param {string} question
 * @param {string} sessionId
 * @param {Object} options - evaluate / allowRetry / maxRetries, as queryAgent
 * @param {Object} handlers
 * @param {(event: Object) => void} handlers.onEvent - called for every event in order
 * @param {AbortSignal} handlers.signal - abort to cancel the run and close the socket
 * @returns {Promise<Object|null>} the terminal `done` event, or null if it never arrived
 */
export function streamQueryWs(question, sessionId = 'default', options = {}, handlers = {}) {
  const { evaluate = false, allowRetry = false, maxRetries = 0 } = options;
  const { onEvent = () => {}, signal } = handlers;

  return new Promise((resolve, reject) => {
    const wsUrl = `${API_BASE_URL.replace(/^http/, 'ws')}/api/query/ws`;
    let socket;
    try {
      socket = new WebSocket(wsUrl);
    } catch (err) {
      reject(err);
      return;
    }

    let done = null;
    // Until the server acknowledges with run_started, a failure means the transport itself
    // is unusable (proxy stripped the Upgrade, endpoint missing) and the caller should fall
    // back to SSE. After that point, a failure is a real error for this run.
    let started = false;
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      fn(value);
    };

    function onAbort() {
      // Cancel on the same connection — the reason this transport is preferred.
      try {
        socket.send(JSON.stringify({ type: 'cancel' }));
      } catch {
        /* socket already closing */
      }
      try {
        socket.close();
      } catch {
        /* ignore */
      }
    }

    if (signal) {
      if (signal.aborted) {
        try { socket.close(); } catch { /* ignore */ }
        const err = new Error('Aborted');
        err.name = 'AbortError';
        reject(err);
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    socket.onopen = () => {
      socket.send(JSON.stringify({
        type: 'start',
        token: getToken(),
        question,
        session_id: sessionId,
        evaluate,
        allow_retry: allowRetry,
        max_retries: maxRetries,
      }));
    };

    socket.onmessage = (message) => {
      let event;
      try {
        event = JSON.parse(message.data);
      } catch {
        return; // never let one malformed frame kill the stream
      }
      if (event.type === 'run_started') started = true;
      onEvent(event);
      if (event.type === 'done') done = event;
    };

    socket.onerror = () => {
      if (!started) finish(reject, new Error('WebSocket connection failed'));
    };

    socket.onclose = (event) => {
      if (started) {
        finish(resolve, done);
        return;
      }
      // 1008 is what the server uses for a rejected token or a failed guard.
      const detail = event.reason || `code ${event.code}`;
      finish(reject, new Error(
        event.code === 1008 ? `WebSocket rejected: ${detail}` : `WebSocket closed before starting (${detail})`
      ));
    };
  });
}

/**
 * Stream a query over SSE. Fallback for environments that will not pass a WebSocket
 * Upgrade (a proxy hop, a CDN), and the transport the platform can consume with no
 * socket-bridging work on their side.
 *
 * POST rather than GET so the question stays out of URLs and access logs; read with a
 * stream reader rather than EventSource so the JWT rides in the Authorization header.
 *
 * @param {string} question
 * @param {string} sessionId
 * @param {Object} options - evaluate / allowRetry / maxRetries, as queryAgent
 * @param {Object} handlers
 * @param {(event: Object) => void} handlers.onEvent - called for every event in order
 * @param {AbortSignal} handlers.signal - abort to stop reading (see cancelStream to stop the work)
 * @returns {Promise<Object|null>} the terminal `done` event, or null if it never arrived
 */
export async function streamQuery(question, sessionId = 'default', options = {}, handlers = {}) {
  const { evaluate = false, allowRetry = false, maxRetries = 0 } = options;
  const { onEvent = () => {}, signal } = handlers;

  const response = await fetch(`${API_BASE_URL}/api/query/stream`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({
      question,
      session_id: sessionId,
      evaluate,
      allow_retry: allowRetry,
      max_retries: maxRetries,
    }),
    signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done = null;

  // SSE frames are separated by a blank line. A chunk can split a frame anywhere, so
  // everything after the last blank line stays buffered until the rest arrives.
  while (true) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buffer += decoder.decode(value, { stream: true });

    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      if (!frame.trim() || frame.startsWith(':')) continue; // ':' == keepalive comment
      const dataLine = frame
        .split('\n')
        .find((line) => line.startsWith('data: '));
      if (!dataLine) continue;
      let event;
      try {
        event = JSON.parse(dataLine.slice('data: '.length));
      } catch {
        continue; // never let one malformed frame kill the stream
      }
      onEvent(event);
      if (event.type === 'done') done = event;
    }
  }

  return done;
}

/**
 * Stop a running streamed query server-side, so it stops spending tokens.
 * Aborting the fetch alone only stops us listening.
 * @param {string} runId - from the `run_started` event
 */
export async function cancelStream(runId) {
  if (!runId) return;
  await fetch(`${API_BASE_URL}/api/query/stream/${runId}/cancel`, {
    method: 'POST',
    headers: authHeaders(),
  }).catch(() => {}); // best-effort: the run also dies when the connection drops
}

/**
 * List OpenSearch indices visible to the authenticated company.
 * @returns {Promise} List of indices
 */
export async function listIndices() {
  const response = await fetch(`${API_BASE_URL}/api/indices`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Health check
 * @returns {Promise} Health status
 */
export async function healthCheck() {
  const response = await fetch(`${API_BASE_URL}/api/health`);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Get token usage statistics for the current session
 * @returns {Promise} Token usage data with history
 */
export async function getTokenUsage() {
  const response = await fetch(`${API_BASE_URL}/api/token-usage/current`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Clear token usage history
 * @returns {Promise} Success response
 */
export async function clearTokenUsage() {
  const response = await fetch(`${API_BASE_URL}/api/token-usage/clear`, {
    method: 'POST',
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * List all persisted conversations (newest first).
 * @returns {Promise<Array>} Array of {session_id, title, created_at, last_updated}
 */
export async function listConversations() {
  const response = await fetch(`${API_BASE_URL}/api/conversations`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Get full detail for a single conversation including messages.
 * @param {string} sessionId
 * @returns {Promise<Object>} Conversation detail
 */
export async function getConversation(sessionId) {
  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(sessionId)}`,
    { headers: authHeaders() }
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Delete a conversation from persistence and the in-memory cache.
 * @param {string} sessionId
 * @returns {Promise<Object>}
 */
export async function deleteConversation(sessionId) {
  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', headers: authHeaders() }
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Rename a conversation (user-set titles are never overwritten by auto-naming).
 * @param {string} sessionId
 * @param {string} title - 1..120 chars
 */
export async function renameConversation(sessionId, title) {
  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(sessionId)}`,
    { method: 'PATCH', headers: authHeaders(true), body: JSON.stringify({ title }) }
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Submit thumbs feedback for one AI message in a conversation.
 * @param {Object} params
 * @param {string} params.sessionId
 * @param {string} params.messageId
 * @param {'up'|'down'} params.rating
 * @param {string|null} [params.comment]
 * @returns {Promise<Object>}
 */
export async function submitMessageFeedback({
  sessionId,
  messageId,
  rating,
  comment = null,
}) {
  const headers = authHeaders(true);
  headers['Idempotency-Key'] = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        rating,
        comment: comment && comment.trim() ? comment.trim() : null,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}
